<script setup lang="ts">
/** Menu list with steppers. Pick order is kept (the POS counts boxes over the quota from the end). */
import { computed, ref } from 'vue'
import type { PosMenu } from '../../../api/pos/types'
import { baht } from '../../../lib/inbox'
import { qtyOf } from '../../../lib/order'
import { useMenuRead } from '../../../stores/menuRead'
import { useOrders } from '../../../stores/order'
import Icon from '../../ui/Icon.vue'

const props = defineProps<{ convId: string }>()
const orders = useOrders()
const mr = useMenuRead()

const f = computed(() => orders.forms[props.convId] ?? null)
const term = ref('')
const onlyPicked = computed(() => !!mr.onlyPicked[props.convId]) // shared with the expanded grid (MenuGrid.vue)

const shown = computed<PosMenu[]>(() => {
  const all = orders.catalog?.menus ?? []
  const t = term.value.trim().toLowerCase()
  return all.filter((m) => {
    if (onlyPicked.value && !qty(m.id)) return false
    if (!t) return true
    return m.name.toLowerCase().includes(t) || m.code.toLowerCase().includes(t)
  })
})

function qty(id: number): number {
  return f.value ? qtyOf(f.value, id) : 0
}

function set(id: number, n: number): void {
  orders.setQty(props.convId, id, n)
}

function typed(id: number, e: Event): void {
  const el = e.target as HTMLInputElement
  const n = Number(el.value.replace(/\D/g, '') || '0')
  set(id, n)
  el.value = String(qty(id))
}

function sub(m: PosMenu): string | null {
  if (m.type === 'snacks') return `ของว่าง ${baht(m.price)} · ไม่นับในแพ็ก`
  if (m.plus > 0) return `+${baht(m.plus)} ต่อกล่อง`
  return null
}
</script>

<template>
  <div class="mp">
    <div class="mp-h">
      <label class="inp mp-s"
        ><Icon name="search" /><input v-model="term" type="search" aria-label="ค้นหาเมนู" placeholder="ค้นหาเมนู" autocomplete="off"
      /></label>
      <button class="tlink" :class="{ ac: onlyPicked }" :aria-pressed="onlyPicked" @click="mr.toggleOnlyPicked(convId)">
        <Icon name="check" />เฉพาะที่เลือก
      </button>
    </div>
    <div class="ml scroll" role="list" aria-label="เมนู">
      <div v-for="m in shown" :key="m.id" class="mi" :class="{ on: qty(m.id) > 0 }" role="listitem">
        <span class="mi-b">
          <span class="mi-n">{{ m.name }}</span>
          <span v-if="sub(m)" class="mi-s">{{ sub(m) }}</span>
        </span>
        <span class="stp">
          <button class="stp-b" :disabled="qty(m.id) === 0" :aria-label="`ลด ${m.name}`" @click="set(m.id, qty(m.id) - 1)">
            <svg class="ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14" /></svg>
          </button>
          <input
            class="stp-v"
            type="text"
            inputmode="numeric"
            :value="qty(m.id)"
            :aria-label="`จำนวน ${m.name}`"
            @change="typed(m.id, $event)"
            @focus="($event.target as HTMLInputElement).select()"
          />
          <button class="stp-b" :aria-label="`เพิ่ม ${m.name}`" @click="set(m.id, qty(m.id) + 1)"><Icon name="plus" /></button>
        </span>
      </div>
      <p v-if="!shown.length" class="mp-empty">{{ onlyPicked ? 'ยังไม่ได้เลือกเมนู' : 'ไม่พบเมนูนี้' }}</p>
    </div>
  </div>
</template>

<style scoped>
.mp {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mp-h {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mp-s {
  flex: 1 1 auto;
}
.ml {
  border: 1px solid var(--line);
  border-radius: 10px;
  max-height: 300px;
  overflow-y: auto;
}
.mi {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 5px 8px 5px 12px;
}
.mi + .mi {
  border-top: 1px dashed #e3eae6;
}
.mi.on {
  background: #f5faf7;
}
.mi-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.mi-n {
  font-size: 13px;
  line-height: 20px;
}
.mi.on .mi-n {
  font-weight: 600;
}
.mi-s {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.stp {
  flex: none;
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.mi.on .stp {
  border-color: #9fcdb8;
}
.stp-b {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ac2);
}
.stp-b:hover:not(:disabled) {
  background: var(--acs);
}
.stp-b:disabled {
  color: #b5c2bc;
}
.stp-b .ic {
  width: 14px;
  height: 14px;
  stroke-width: 2.2;
}
.stp-v {
  width: 36px;
  height: 30px;
  border: 0;
  border-left: 1px solid var(--line2);
  border-right: 1px solid var(--line2);
  text-align: center;
  font: 600 13px/20px var(--mono);
  color: var(--ink);
  outline: none;
  background: transparent;
}
.stp-v:focus {
  background: #f5faf7;
}
.mp-empty {
  padding: 16px;
  text-align: center;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
</style>
